import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, map } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { TranslationService } from '../../../shared/services/translation.service';
import { OrderByDirection, OrderBy, UNDERSCORE } from '../../../shared/utils/constant';
import { OfferDraft } from '../../models/offer';
import { AppWizardService } from '../../services/app-wizard.service';
import { OfferService } from '../../services/offer.api.service';
import { DialogDeleteDraftComponent,
	RESPONSE_CONFIRM } from '../../../shared/components/dialog-delete-draft/dialog-delete-draft.component';
import {
	SnackbarDeleteDraftComponent
} from '../../../shared/components/snackbar-delete-draft/snackbar-delete-draft.component';
import { LocationService } from '../../../shared/services/location.service';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

const PREFIX_OFFER_PRODUCT_ID_EOFFER = 'e';

@Component({
	selector: 'copo-offer-draft-list',
	templateUrl: './offer-draft-list.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		PageHeaderComponent,
		NgIf,
		VirtualScrollerDirective,
		NgFor,
		MatIconModule,
		TranslateModule,
		MatRippleModule,

		MatTooltipModule,

		MatButtonModule,
		LoadingSpinnerComponent,
		DatePipe
	]
})
export class OfferDraftListComponent implements OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;
	readonly UNDERSCORE = UNDERSCORE;
	isLoading = false;
	offerDraft?: OfferDraft;
	requiredFields: (keyof OfferDraft)[] = [
		'id',
		'customerOfferReference',
		'creationDate',
		'modificationDate',
		'lines',
		'offerFamilyVolumeList',
		'offerType'
	];

	filters = '';
	sortWay = OrderByDirection.DESC;
	sortBy: keyof OfferDraft = 'modificationDate';
	deleteDraftSubscription?: Subscription;

	constructor(
		private readonly offerService: OfferService,
		private readonly router: Router,
		private readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService,

		private readonly dialog: MatDialog,

		private readonly snackBar: MatSnackBar,
		private readonly locationService: LocationService
	) {}

	get dataId() {
		return `offer-draft-list-${
			this.sortWay
		}-${
			this.sortBy
		}`;
	}

	ngOnDestroy() {
		if (this.deleteDraftSubscription) {
			this.deleteDraftSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<OfferDraft[]>) => void) {
		provideData(
			this.offerService.getDraftOffers(
				this.requiredFields,
				this.filters,
				index,
				limit,
				new OrderBy(this.sortBy, this.sortWay),
				false
			).pipe(map(res => res.results.map((offer: OfferDraft) => {
				this.offerDraft = offer;
				return offer;
			})))
		);
	}

	getOfferDraftName(offer: OfferDraft) {
		if (offer.customerOfferReference) {
			return `${ this.translationService.getTR('draft.value') }
			${ offer.customerOfferReference
			}`;
		}
		return this.translationService.getTR('draft.value');
	}

	getSelectedProduct(offer: OfferDraft) {
		return offer.lines?.length;
	}

	getTotalTons(offer: OfferDraft) {
		let quantity = 0;
		if (offer.lines) {
			for (const conf of offer.lines) {
				quantity = conf.requestedQuantity ? quantity + conf.requestedQuantity : quantity + 0;
			}
		}
		if (offer.offerFamilyVolumeList) {
			for (const conf of offer.offerFamilyVolumeList) {
				quantity = conf.quantity ? quantity + conf.quantity : quantity + 0;
			}
		}
		return Number.isNaN(quantity) ? 0 : quantity;
	}

	async evtGoToOfferCard(draftId: number) {
		await this.router.navigateByUrl(`offer/card/${ PREFIX_OFFER_PRODUCT_ID_EOFFER }${ draftId }`);
	}

	async evtContinueDraft(draftId: number) {
		this.isLoading = true;
		await this.appWizardService.actionEditOffer(`${ PREFIX_OFFER_PRODUCT_ID_EOFFER }${ draftId }`);
		this.isLoading = false;
	}

	evtDeleteDraftOffer(draftId: number, draftName: string) {
		const path = '/offer/draft';
		const sub = this.dialog.open<DialogDeleteDraftComponent, number>(
			DialogDeleteDraftComponent,
			{ panelClass: 'copo-offer-dialog' }
		);
		sub.componentInstance.draftName = draftName;
		this.deleteDraftSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM) {
					try {
						await this.offerService.deleteDraftOffers(draftId).then(async () => {
							this.snackBar.openFromComponent(
								SnackbarDeleteDraftComponent,
								{
									data: this.translationService.getTR('draft.deleted.successfully'),
									duration: 5000,
									panelClass: ['copo-draft-snackbar']
								}
							);
							await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
						});
					} catch (err) {
						console.error('unable to delete draft', err);
						this.snackBar.open(
							this.translationService.getTR(
								'error.deleting.draft'
							),
							undefined,
							{
								duration: 5000,
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					}
				}
			});
	}

	goBack() {
		this.locationService.back();
	}
}
